/**
 * iOS Clipboard Handler
 * Handles clipboard operations for iOS devices with deep link parameters
 */

export const handleiOSClipboard = async (i, b) => {
  try {
    // Create data object with parameters and timestamp
    const data = {
      i,
      b,
      timestamp: Date.now()
    };

    // Convert to JSON string and encode as base64
    const jsonString = JSON.stringify(data);
    const base64Data = btoa(jsonString);
    const clipboardData = `NIVOHUB:${base64Data}`;

    // Copy to clipboard with fallback methods
    await copyToClipboardWithFallback(clipboardData);
    
    console.log('iOS clipboard data set successfully');
    return { success: true, message: 'Opening App Store...' };

  } catch (error) {
    console.error('iOS clipboard handler failed:', error);
    return { success: false, message: 'Setup failed. Opening App Store...' };
  }
};

/**
 * Copy text to clipboard with multiple fallback methods
 */
const copyToClipboardWithFallback = async (text) => {
  console.log('🔧 Starting clipboard copy process...');
  console.log('📋 Text to copy:', `${text.substring(0, 50)}...`);
  console.log('🔒 Secure context:', window.isSecureContext);
  console.log('📱 User agent:', navigator.userAgent);
  
  const errors = [];

  // Method 1: Modern Clipboard API (preferred)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      console.log('🚀 Trying modern clipboard API...');
      await navigator.clipboard.writeText(text);
      console.log('✅ Modern clipboard API succeeded');
      return;
    } catch (error) {
      console.warn('❌ Modern clipboard API failed:', error);
      errors.push(`Modern API: ${error.message}`);
    }
  } else {
    const reason = !navigator.clipboard ? 'navigator.clipboard not available' : 'not secure context';
    console.warn('⚠️ Modern clipboard API not available:', reason);
    errors.push(`Modern API: ${reason}`);
  }

  // Method 2: Document.execCommand fallback
  try {
    console.log('🔄 Trying execCommand fallback...');
    await copyUsingExecCommand(text);
    console.log('✅ execCommand succeeded');
    return;
  } catch (error) {
    console.warn('❌ execCommand failed:', error);
    errors.push(`execCommand: ${error.message}`);
  }

  // Method 3: iOS-specific textarea fallback
  try {
    console.log('📱 Trying iOS-specific fallback...');
    await copyUsingIOSFallback(text);
    console.log('✅ iOS fallback succeeded');
    return;
  } catch (error) {
    console.warn('❌ iOS fallback failed:', error);
    errors.push(`iOS fallback: ${error.message}`);
  }

  // If all automatic methods failed, don't show fallback UI
  // Just fail gracefully and let the app continue to App Store
  console.error('💥 All automatic clipboard methods failed:', errors);
  throw new Error(`All clipboard methods failed: ${errors.join(', ')}`);
};

/**
 * Copy using document.execCommand (legacy method)
 */
const copyUsingExecCommand = text => new Promise((resolve, reject) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  
  // Make textarea invisible but focusable
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  textArea.style.opacity = '0';
  textArea.style.pointerEvents = 'none';
  
  document.body.appendChild(textArea);
  
  try {
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, text.length);
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      resolve();
    } else {
      reject(new Error('execCommand returned false'));
    }
  } catch (error) {
    document.body.removeChild(textArea);
    reject(error);
  }
});

/**
 * iOS-specific fallback method with user interaction trigger
 */
const copyUsingIOSFallback = text => new Promise((resolve, reject) => {
  // iOS requires user interaction for clipboard access
  // We'll try a different approach that might work better
  
  const textArea = document.createElement('input');
  textArea.type = 'text';
  textArea.value = text;
  
  // Make it invisible but accessible (iOS Safari specific)
  textArea.style.position = 'absolute';
  textArea.style.left = '-9999px';
  textArea.style.top = '-9999px';
  textArea.style.opacity = '0';
  textArea.style.pointerEvents = 'none';
  textArea.style.fontSize = '16px'; // Prevent zoom
  textArea.style.width = '1px';
  textArea.style.height = '1px';
  textArea.style.border = 'none';
  textArea.style.outline = 'none';
  textArea.readOnly = false;
  
  document.body.appendChild(textArea);
  
  // Use a different approach for iOS
  const attemptCopy = () => {
    try {
      // Focus the input
      textArea.focus();
      
      // Select all text
      textArea.select();
      textArea.setSelectionRange(0, text.length);
      
      // Try to copy - this might work in iOS if called immediately after user interaction
      const successful = document.execCommand('copy');
      
      // Clean up
      if (document.body.contains(textArea)) {
        document.body.removeChild(textArea);
      }
      
      if (successful) {
        console.log('🎉 iOS clipboard copy succeeded via execCommand');
        resolve();
      } else {
        reject(new Error('iOS fallback execCommand returned false'));
      }
    } catch (error) {
      if (document.body.contains(textArea)) {
        document.body.removeChild(textArea);
      }
      reject(error);
    }
  };
  
  // Try immediately (might work if called during user gesture)
  attemptCopy();
});


/**
 * Redirect to App Store after clipboard operation
 */
export const redirectToAppStore = (appStoreURL, delay = 1000) => {
  if (!appStoreURL) {
    console.error('App Store URL not provided');
    return;
  }

  setTimeout(() => {
    try {
      window.location.href = appStoreURL;
    } catch (error) {
      console.error('Failed to redirect to App Store:', error);
      // Fallback: open in new window
      window.open(appStoreURL, '_blank');
    }
  }, delay);
};

/**
 * Validate iOS deep link parameters
 */
export const validateiOSParameters = (i, b) => {
  if (!i || !b) {
    return { valid: false, error: 'Missing required parameters i or b' };
  }

  // Basic UUID validation
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  if (!uuidRegex.test(i)) {
    return { valid: false, error: 'Parameter i is not a valid UUID' };
  }
  
  if (!uuidRegex.test(b)) {
    return { valid: false, error: 'Parameter b is not a valid UUID' };
  }

  return { valid: true };
};
